import { Component, EventEmitter, OnInit, Output, OnDestroy, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Subscription, tap } from 'rxjs';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { ConsolidationLedgersService } from './consolidation-ledgers.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-consolidation-ledgers',
    templateUrl: './consolidation-ledgers.component.html',
    styleUrls: ['./consolidation-ledgers.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ConsolidationLedgersComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;

  // Add initialization guard to prevent multiple API calls
  private isInitialized = false;
  private initializationInProgress = false;
  private lastInitData: any = null;

  // Entity data properties
  entityList: any[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';

  // Component specific properties
  loadingData = true;
  variance = 0;
  tableHeader = [
    "ME Code",
    "Account",
    "Account Desc",
    "Amount"
  ];
  tabList = [
    "Income Statement",
    "Balance Sheet"
  ];
  tabName = "Income Statement";
  activeTab = 1;
  tabs = [
    { id: 1, name: "Income Statement" },
    { id: 2, name: "Balance Sheet" }
  ];

  // Data arrays
  incomeStmtList: any[] = [];
  incomeStmtListBackup: any[] = [];
  balanceSheetList: any[] = [];
  catDropdown: any[] = [];
  noRecords = false;

  constructor(
    private _snackBar: MatSnackBar,
    private jsonObjectFactoryService: JsonObjectFactoryService,
    private consolidationLedgersService: ConsolidationLedgersService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.isInitialized = false;
    this.initializationInProgress = false;
    this.lastInitData = null;
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const newRowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);

    const newDataKey = JSON.stringify({
      leid: newRowData?.LEID,
      cdr_no: newRowData?.CDR_NO,
      me_code: newRowData?.ME_CODE,
      ledger: newRowData?.LEDGER
    });
    if (this.lastInitData !== newDataKey) {
      this.isInitialized = false;
      this.initializationInProgress = false;
    }

    this.rowData = newRowData;
    this.cdr.markForCheck();
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || '';
  }

  init() {
    try {
      // Guard: prevent HTTP calls with undefined base_url
      if (!this.resolveApiBase()) {
        console.log('[ConsolidationLedgers] base_url not yet available, skipping init');
        return;
      }

      // Create a unique key for this initialization based on critical data
      const currentInitKey = JSON.stringify({
        leid: this.rowData?.LEID,
        cdr_no: this.rowData?.CDR_NO,
        me_code: this.rowData?.ME_CODE,
        ledger: this.rowData?.LEDGER
      });

      // Prevent multiple initializations with same data
      if (this.isInitialized && this.lastInitData === currentInitKey) {
        console.log('Component already initialized with same data, skipping...');
        return;
      }

      if (this.initializationInProgress) {
        console.log('Initialization already in progress, skipping...');
        return;
      }

      if (this.rowData && Object.keys(this.rowData).length > 0) {
        console.log('Starting initialization with data:', currentInitKey);
        this.initializationInProgress = true;
        this.lastInitData = currentInitKey;
        this.setupEntityData(); // ALWAYS call this first
        this.loadData();
      }
    } catch (e) {
      console.error('Error in ConsolidationLedgers init:', e);
      this.initializationInProgress = false;
    }
  }

  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.CDR_NO;
    this.leType = this.rowData.LE_TYPE;

    // 🔴 MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  loadData() {
    this.showFormLoader = true;
    this.loadingData = true;

    // Use forkJoin to wait for all API calls to complete
    const incStmtCall = this.fetchIncStmtData();
    const balSheetCall = this.fetchBalSheetData();
    const cateListCall = this.fetchCateList();

    // Wait for all calls to complete
    forkJoin([incStmtCall, balSheetCall, cateListCall]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.showFormLoader = false;
        this.loadingData = false;
        this.isInitialized = true;
        this.initializationInProgress = false;
        console.log('All data loaded successfully');
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.showFormLoader = false;
        this.loadingData = false;
        this.initializationInProgress = false;
      }
    });
  }

  fetchIncStmtData() {
    const params = {
      "action_code": 'cdc47d',
      "sso_id": this.USER_SETTINGS?.user?.sso_id,
      "tax_year": this.filterParams.tax_year || this.globalService?.tax_year,
      "me_codes": this.rowData.ME_CODE,
      "le_key": this.rowData.LE_KEY,
      "cdr_no": this.rowData.CDR_NO,
      "ledger": this.rowData.LEDGER,
      "combination_key": this.rowData.COMBINATION_KEY,
      "scenario": null,
      "jcd_key": this.rowData.JCD_KEY || this.globalService?.jcd_key
    };

    return this.jsonObjectFactoryService.getJSONObj(
      this.resolveApiBase() + '/loadJsonObject',
      params,
      this.globalService
    ).pipe(
      tap((data: any) => {
        this.incomeStmtList = JSON.parse(JSON.stringify(data.jsonObject));
        const map = new Map();

        // To get total amount for each account
        for(let i = 0; i < this.incomeStmtList.length; i++) {
          if(map != null && map.has(this.incomeStmtList[i].ACCOUNT)){
            const amountMap = parseInt(map.get(this.incomeStmtList[i].ACCOUNT)) + parseInt(this.incomeStmtList[i].AMOUNT);
            map.set(this.incomeStmtList[i].ACCOUNT, amountMap);
          } else {
            map.set(this.incomeStmtList[i].ACCOUNT, parseInt(this.incomeStmtList[i].AMOUNT));
          }
          this.incomeStmtList[i].AMOUNT = this.incomeStmtList[i].AMOUNT + "";
        }

        // Add the total amount of an account to each data row line
        for(let j = 0; j < this.incomeStmtList.length; j++) {
          this.incomeStmtList[j].TOTAL_AMOUNT = parseInt(map.get(this.incomeStmtList[j].ACCOUNT));
          this.incomeStmtList[j].IS_DISPLAY = true;
        }

        // Sort the list in the order of the account
        this.incomeStmtList.sort(this.compare);
        this.incomeStmtListBackup = _.cloneDeep(this.incomeStmtList);
        this.getVariance();

        // Ensure category values are properly formatted for dropdown display
        this.formatCategoryValues();

        if(Array.isArray(this.incomeStmtList) && this.incomeStmtList.length > 0) {
          this.noRecords = false;
        } else {
          this.noRecords = true;
        }
      })
    );
  }

  fetchBalSheetData() {
    const params = {
      "action_code": 'p7xrsm',
      "sso_id": this.USER_SETTINGS?.user?.sso_id,
      "tax_year": this.filterParams.tax_year || this.globalService?.tax_year,
      "scenario": null,
      "jcd_key": this.rowData.JCD_KEY || this.globalService?.jcd_key,
      "me_codes": this.rowData.ME_CODE,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    return this.jsonObjectFactoryService.getJSONObj(
      this.resolveApiBase() + '/loadJsonObject',
      params,
      this.globalService
    ).pipe(
      tap((data: any) => {
        this.balanceSheetList = JSON.parse(JSON.stringify(data.jsonObject));
      })
    );
  }

  fetchCateList() {
    const params = {
      "action_code": '4l2j4c',
      "sso_id": this.USER_SETTINGS?.user?.sso_id,
      "tax_year": this.filterParams.tax_year || this.globalService?.tax_year,
      "scenario": null,
      "jcd_key": this.rowData.JCD_KEY || this.globalService?.jcd_key
    };

    return this.jsonObjectFactoryService.getJSONObj(
      this.resolveApiBase() + '/loadJsonObject',
      params,
      this.globalService
    ).pipe(
      tap((data: any) => {
        this.catDropdown = data.jsonObject;
      })
    );
  }

  // To get the track by as combination key
  itemTracker(item: any) {
    return item.ACCOUNT + '-' + item.OCCURRENCE;
  }

  // Ensure category values are properly formatted for dropdown
  private formatCategoryValues() {
    if (this.incomeStmtList && this.incomeStmtList.length > 0) {
      this.incomeStmtList.forEach(item => {
        // Handle various empty/null states and convert to empty string for default selection
        if (item.CATEGORY === null || item.CATEGORY === undefined || item.CATEGORY === '0' || item.CATEGORY === 0) {
          item.CATEGORY = '';
        } else if (item.CATEGORY !== '') {
          // Convert to string to ensure proper binding
          item.CATEGORY = String(item.CATEGORY);
        }
      });
    }
  }

  // Format NET_INCM with parentheses for negative values (matching AngularJS behavior)
  formatNetIncome(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const num = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(num)) {
      return value.toString();
    }

    // Format with commas and parentheses for negative values
    if (num < 0) {
      return '(' + Math.abs(num).toLocaleString('en-US') + ')';
    } else {
      return num.toLocaleString('en-US');
    }
  }

  // Format number with commas for amount inputs
  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    // Convert to number if it's a string
    const num = typeof value === 'string' ? parseFloat(value) : value;

    // Check if it's a valid number
    if (isNaN(num)) {
      return value.toString();
    }

    // Format number with commas every 3 digits
    return num.toLocaleString('en-US');
  }

  // Remove commas from formatted number string
  removeCommas(value: string): string {
    return value.replace(/,/g, '');
  }

  // Handle input formatting with commas
  onAmountInput(event: any, row: any) {
    const input = event.target;
    const value = input.value;

    // Remove commas and non-numeric characters except minus sign
    const cleanValue = value.replace(/[^0-9.-]/g, '');

    // Update the row amount with clean value
    row.AMOUNT = cleanValue;

    // Format the display value with commas
    if (cleanValue !== '' && !isNaN(parseFloat(cleanValue))) {
      input.value = this.formatNumber(cleanValue);
    } else {
      input.value = cleanValue;
    }

    // Call the original valueChanged method
    this.valueChanged(row);
  }

  getVariance() {
    let total = 0;
    for(let i = 0; i < this.incomeStmtList.length; i++) {
      // Remove commas before parsing
      const cleanAmount = this.removeCommas(this.incomeStmtList[i].AMOUNT);
      if(parseInt(cleanAmount) == undefined || parseInt(cleanAmount) == null) {
        this.incomeStmtList[i].AMOUNT = "0";
      } else {
        this.incomeStmtList[i].AMOUNT = cleanAmount;
      }
      if(this.incomeStmtList[i].IS_DISPLAY) {
        total = total + parseInt(this.incomeStmtList[i].AMOUNT);
      }
    }
    this.variance = this.rowData.NET_INCM - total;
  }

  // Value change listener for input text
  valueChanged(row: any) {
    const localList = this.incomeStmtList.filter(item => row.ACCOUNT == item.ACCOUNT && item.OCCURRENCE != 1 && item.IS_DISPLAY);
    let total = 0;
    for(let i = 0; i < localList.length; i++) {
      // Remove commas before parsing
      const cleanAmount = this.removeCommas(localList[i].AMOUNT);
      if(parseInt(cleanAmount) == undefined || parseInt(cleanAmount) == null || cleanAmount == "") {
        localList[i].AMOUNT = "0";
      } else {
        localList[i].AMOUNT = cleanAmount;
      }
      total = total + parseInt(localList[i].AMOUNT);
    }

    // Remove commas from current row amount
    const cleanRowAmount = this.removeCommas(row.AMOUNT);
    row.AMOUNT = parseInt(cleanRowAmount) + ""; // To remove zeros

    const localObj1 = this.incomeStmtList.filter(item => row.ACCOUNT == item.ACCOUNT && item.OCCURRENCE == 1 && item.IS_DISPLAY);
    localObj1[0].AMOUNT = (parseInt(localObj1[0].TOTAL_AMOUNT) - total) + "";
    const index = this.incomeStmtList.findIndex((item) => row.ACCOUNT == item.ACCOUNT && item.OCCURRENCE == 1 && item.IS_DISPLAY);
    localObj1[0].type = 'U';
    this.incomeStmtList[index] = localObj1[0];
    if(row.type != 'I') {
      row.type = 'U';
    }
    row.AMOUNT = parseInt(cleanRowAmount) + "";

    this.getVariance();
  }

  // Value change of the category field
  valueCategoryChanged(row: any) {
    const localObj = this.incomeStmtList.filter(item => row.ACCOUNT == item.ACCOUNT
        && item.CATEGORY == row.CATEGORY && item.OCCURRENCE != row.OCCURRENCE && item.IS_DISPLAY);

    if(localObj != null && localObj.length > 0) {
      this._snackBar.open("Category cannot be same for the given ACCOUNT = " + row.ACCOUNT, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      row.CATEGORY = "";
      return;
    }
    if(row.type != 'I') {
      row.type = 'U';
    }
  }

  // Add new row method
  addRow(row: any) {
    const localObj = this.incomeStmtList.filter(item => row.ACCOUNT == item.ACCOUNT
        && ((this.removeCommas(item.AMOUNT) == "0" && item.OCCURRENCE != '1') || item.CATEGORY == null || item.CATEGORY == "" || item.CATEGORY == "0") && item.IS_DISPLAY);

    if(localObj != null && localObj.length > 0) {
      this._snackBar.open("Please fill amount and category details for the ACCOUNT = " + row.ACCOUNT, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const row1 = _.cloneDeep(row);
    const filterAccountObj = this.incomeStmtList.filter(item => row.ACCOUNT == item.ACCOUNT);
    const maxValue = Math.max.apply(Math, filterAccountObj.map(function(item) {
      return item.OCCURRENCE;
    }));
    row1.OCCURRENCE = (maxValue + 1).toString();
    row1.type = "I";
    row1.CATEGORY = ""; // Empty string for default "-- please select One --" option
    row1.AMOUNT = "0";
    this.incomeStmtList.push(row1);
    this.incomeStmtList.sort(this.compare);
    this.getVariance();
  }

  // Delete row method
  deleteRow(row: any) {
    const localObj = this.incomeStmtList.filter(item => row.ACCOUNT == item.ACCOUNT && item.OCCURRENCE == 1 && item.IS_DISPLAY);
    const cleanLocalAmount = this.removeCommas(localObj[0].AMOUNT);
    const cleanRowAmount = this.removeCommas(row.AMOUNT);
    localObj[0].AMOUNT = parseInt(cleanLocalAmount) + parseInt(cleanRowAmount) + "";
    if(row.type != 'I') {
      row.type = "D";
      localObj[0].type = 'U';
      const index = this.incomeStmtList.findIndex((item) => row.ACCOUNT == item.ACCOUNT && item.OCCURRENCE == row.OCCURRENCE && item.IS_DISPLAY);
      row.IS_DISPLAY = false;
      this.incomeStmtList[index] = row;
    } else {
      this.incomeStmtList = this.incomeStmtList.filter(item => !(row.OCCURRENCE == item.OCCURRENCE && row.ACCOUNT == item.ACCOUNT));
    }
    const index = this.incomeStmtList.findIndex((item) => row.ACCOUNT == item.ACCOUNT && item.OCCURRENCE == 1);
    this.incomeStmtList[index] = localObj[0];
    this.getVariance();
  }

  // This function is used for sorting by account number
  compare(a: any, b: any) {
    let comparison = 0;
    if (parseInt(a.ACCOUNT) > parseInt(b.ACCOUNT)) {
      comparison = 1;
    } else if (parseInt(a.ACCOUNT) < parseInt(b.ACCOUNT)) {
      comparison = -1;
    } else if(a.OCCURRENCE == '1') {
      comparison = 1;
    }
    return comparison;
  }

  reset() {
    this.incomeStmtList = _.cloneDeep(this.incomeStmtListBackup);
    this.formatCategoryValues(); // Ensure category values are properly formatted
    this.getVariance();
    this.tabName = "Income Statement";
    this.activeTab = 1;
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const emptyValuesList = this.incomeStmtList.filter(item => ((this.removeCommas(item.AMOUNT) == "0" && item.OCCURRENCE != '1')) && item.IS_DISPLAY);
    if(emptyValuesList != null && emptyValuesList.length > 0) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open("Please fill amount details before saving.", 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const saveList = this.incomeStmtList.filter(item => item.type != undefined && item.type != null && item.type != "");
    if(Object.keys(saveList).length === 0) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open("No data has been changed.", 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const returnClobSettingsObj: any = {};
    const ledgersFormSaveDtls: any[] = [];
    const groupedByAccount = this.groupBy(saveList, 'ACCOUNT');
    const groupArray: any[] = [];

    for (const groupName in groupedByAccount) {
      groupArray.push({ACCOUNT: groupName, accList: groupedByAccount[groupName]});
    }

    for(let k = 0; k < groupArray.length; k++) {
      const account = groupArray[k].ACCOUNT;
      const localAccList = groupArray[k].accList;
      const returnObj: any = {};
      returnObj['leid'] = this.rowData.LEID;
      returnObj['cdr_no'] = this.rowData.CDR_NO;
      returnObj['me_code'] = this.rowData.ME_CODE;
      returnObj['ledger'] = this.rowData.LEDGER;
      returnObj['acct_ref_key'] = account;
      returnObj['code_combination'] = this.rowData.COMBINATION_KEY;
      returnObj['trans_header_key'] = saveList[k].TRANS_HEADER_KEY;
      returnObj['group_trans_key'] = saveList[k].GROUP_TRANS_KEY;

      const ledgersCatSaveDtls: any[] = [];
      for(let l = 0; l < localAccList.length; l++) {
        const returninnerObj: any = {};
        returninnerObj['category'] = localAccList[l].CATEGORY;
        returninnerObj['amount'] = parseInt(this.removeCommas(localAccList[l].AMOUNT));
        returninnerObj['occurrence'] = localAccList[l].OCCURRENCE;
        returninnerObj['cl_dml_type'] = localAccList[l].type;
        ledgersCatSaveDtls.push(returninnerObj);
      }
      returnObj['catList'] = ledgersCatSaveDtls;
      ledgersFormSaveDtls.push(returnObj);
    }

    returnClobSettingsObj['sso_id'] = this.USER_SETTINGS?.user?.sso_id;
    returnClobSettingsObj['screen_key'] = this.globalService?.screen_key;
    returnClobSettingsObj['tax_year'] = this.filterParams.tax_year || this.globalService?.tax_year;
    returnClobSettingsObj['scenario'] = this.filterParams.scenario || this.globalService?.scenario;
    returnClobSettingsObj['jcd_key'] = this.rowData.JCD_KEY || this.globalService?.jcd_key;

    this.consolidationLedgersService.saveConsLedgers(
      this.resolveApiBase(),
      returnClobSettingsObj,
      ledgersFormSaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Helper method for groupBy functionality
  groupBy(array: any[], prop: string) {
    return array.reduce(function(groups: any, item) {
      const val = item[prop];
      groups[val] = groups[val] || [];
      groups[val].push(item);
      return groups;
    }, {});
  }
}
